import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { Search, Home } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * SaveAsFlexibleModal — Convert a past workout into a "Rutina libre" (free routine).
 * Lets the trainer pick the target client (defaults to the source client) and
 * an optional availability window, then snapshots the workout's exercises.
 *
 * Props:
 *   - workout: source workout (id, client_id, template_name, date)
 *   - onClose: () => void
 *   - onSaved: (workoutId) => void
 */
const SaveAsFlexibleModal = ({ workout, onClose, onSaved }) => {
  const defaultName = workout?.template_name || `Free routine ${workout?.date || ''}`.trim();
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [selectedClientId, setSelectedClientId] = useState(workout?.client_id || null);
  const [name, setName] = useState(defaultName);
  const [availableFrom, setAvailableFrom] = useState('');
  const [availableUntil, setAvailableUntil] = useState('');
  const [trainerNotes, setTrainerNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/users/clients`);
        const list = (res.data || []).filter((u) => u.role === 'client' && (u.client_status || 'active') !== 'inactive');
        if (!cancelled) setClients(list);
      } catch (e) {
        if (!cancelled) setError('Could not load client list');
      } finally {
        if (!cancelled) setLoadingClients(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredClients = useMemo(() => {
    if (!clientSearchTerm.trim()) return clients;
    const q = clientSearchTerm.trim().toLowerCase();
    return clients.filter((c) =>
      (c.full_name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q)
    );
  }, [clients, clientSearchTerm]);

  const dateError = useMemo(() => {
    if (availableFrom && availableUntil && availableUntil < availableFrom) {
      return 'The "until" date must be on or after the "from" date';
    }
    return '';
  }, [availableFrom, availableUntil]);

  const canSubmit = selectedClientId && !dateError && !submitting;

  const handleSave = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${API}/workouts/${workout.id}/save-as-flexible`, {
        name: name.trim() || defaultName,
        client_id: selectedClientId,
        available_from: availableFrom || null,
        available_until: availableUntil || null,
        trainer_notes: trainerNotes || null,
      });
      onSaved?.(res.data?.workout_id, res.data?.exercise_count);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error saving free routine');
    } finally {
      setSubmitting(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/70 z-[9999] overflow-y-auto" onClick={onClose} data-testid="save-flexible-modal">
      <div className="min-h-screen flex items-start sm:items-center justify-center p-4">
        <div className="bg-blue-900/95 rounded-xl max-w-lg w-full my-4 sm:my-8 max-h-[88vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="p-4 border-b border-amber-400/40 flex justify-between items-center bg-amber-900/30">
            <div>
              <h3 className="text-white font-bold text-lg flex items-center gap-2">
                <Home className="w-5 h-5 text-amber-300" />
                Save as Free Routine
              </h3>
              <p className="text-amber-200 text-sm mt-0.5">
                From workout · {workout?.date || ''}
              </p>
            </div>
            <button onClick={onClose} className="text-blue-300 hover:text-white text-xl" data-testid="save-flexible-close-btn">✕</button>
          </div>

          {/* Body */}
          <div className="p-4 overflow-y-auto flex-1">
            <p className="text-amber-200/90 text-sm bg-amber-500/10 border border-amber-400/30 rounded-lg p-3 mb-4">
              <strong>🏠 Rutina libre</strong> — no calendar slot. The client performs it whenever they want. Great for handing a proven session to another client.
            </p>

            {/* Name */}
            <div className="mb-4">
              <label className="block text-blue-200 text-sm font-medium mb-1">Routine name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-field w-full"
                data-testid="save-flexible-name"
              />
            </div>

            {/* Client picker */}
            <div className="mb-4">
              <label className="block text-blue-200 text-sm font-medium mb-1">
                Client <span className="text-red-400">*</span>
                <span className="text-blue-400/60 font-normal"> (defaults to original client)</span>
              </label>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={clientSearchTerm}
                  onChange={(e) => setClientSearchTerm(e.target.value)}
                  placeholder="Search client..."
                  className="input-field w-full pl-10"
                  data-testid="save-flexible-client-search"
                />
              </div>
              {loadingClients ? (
                <p className="text-blue-300 text-sm py-2">Loading clients...</p>
              ) : (
                <div className="space-y-1 max-h-40 overflow-y-auto rounded-lg bg-blue-950/40 p-1">
                  {filteredClients.length === 0 ? (
                    <p className="text-blue-300 text-sm py-2 px-2">No results</p>
                  ) : (
                    filteredClients.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => setSelectedClientId(c.id)}
                        className={`w-full text-left px-3 py-2 rounded transition ${
                          selectedClientId === c.id
                            ? 'bg-amber-500/30 text-white ring-1 ring-amber-300'
                            : 'text-blue-100 hover:bg-blue-800/40'
                        }`}
                        data-testid={`save-flexible-client-${c.id}`}
                      >
                        <div className="text-sm font-medium">
                          {c.full_name}
                          {c.id === workout?.client_id && <span className="text-amber-300 text-xs ml-2">· original</span>}
                        </div>
                        <div className="text-xs text-blue-300">{c.email}</div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Optional date range */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-blue-200 text-xs font-medium mb-1">Available from <span className="text-blue-400/60">(optional)</span></label>
                <input type="date" value={availableFrom} onChange={(e) => setAvailableFrom(e.target.value)} className="input-field w-full text-sm" data-testid="save-flexible-from" />
              </div>
              <div>
                <label className="block text-blue-200 text-xs font-medium mb-1">Until <span className="text-blue-400/60">(optional)</span></label>
                <input type="date" value={availableUntil} onChange={(e) => setAvailableUntil(e.target.value)} className="input-field w-full text-sm" data-testid="save-flexible-until" />
              </div>
            </div>
            {dateError && <p className="text-red-300 text-xs -mt-2 mb-3">{dateError}</p>}

            {/* Trainer notes */}
            <div className="mb-4">
              <label className="block text-blue-200 text-xs font-medium mb-1">Notes for the client <span className="text-blue-400/60">(optional)</span></label>
              <textarea value={trainerNotes} onChange={(e) => setTrainerNotes(e.target.value)} rows={2} className="input-field w-full text-sm" placeholder="E.g. Do it on Saturday morning, rest 60–90s between sets..." data-testid="save-flexible-notes" />
            </div>

            {error && <p className="text-red-300 text-sm bg-red-500/10 border border-red-400/30 rounded p-2 mb-2">{error}</p>}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-blue-400/30 flex gap-2">
            <button onClick={onClose} className="btn-secondary flex-1" data-testid="save-flexible-cancel-btn">Cancel</button>
            <button
              onClick={handleSave}
              disabled={!canSubmit}
              className={`flex-1 px-4 py-2 rounded-lg font-semibold transition ${canSubmit ? 'bg-amber-500 hover:bg-amber-400 text-black' : 'bg-gray-600 text-gray-400 cursor-not-allowed'}`}
              data-testid="save-flexible-submit-btn"
            >
              {submitting ? 'Saving...' : '🏠 Save Free Routine'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SaveAsFlexibleModal;
